import { useNavigate } from "react-router-dom";
import { font, Avatar } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import PortalSwitcher from "./PortalSwitcher";

/**
 * AppHeader — V2 ADR-003 Phase 4.3 wave 3.
 *
 * Extracted from inline JSX in App.js so it can call `useTheme()` and
 * pick up per-portal brand colors. Renders:
 *
 *   - Page title + today's date
 *   - PortalSwitcher (always present; renders nothing for 0 memberships)
 *   - "AI ACTIVE" status badge
 *   - User avatar → click goes to /settings
 *   - Optional workspace context strip below the main bar when the
 *     user is viewing a team workspace
 *
 * Color overrides via useTheme() apply to:
 *   - `goldLight` / `emerald` — paint badges + the workspace strip in
 *     the team's `primary_color`
 *   - `textPrimary` / `navyLight` — paint title text + dark accents in
 *     the team's `accent_color`
 *
 * Surfaces left intentionally on neutral defaults:
 *   - `surface` — header background stays white for predictable contrast
 *   - `border` — divider stays neutral
 *   - `textMuted` — secondary text stays accessible
 *
 * Future hardening (post-Phase 4.3): a contrast-checker on brand-color
 * config so a brokerage can't pick a combo that makes the title
 * illegible. Until then, brand pickers stay on the brokerage side of
 * the responsibility line.
 */
export default function AppHeader({
  user,
  pageTitle,
  activeWorkspace,
  isCompact,
}) {
  const t = useTheme();
  const navigate = useNavigate();

  return (
    <>
      <div style={{
        padding: isCompact ? "14px 16px" : "20px 28px",
        borderBottom: `1.5px solid ${t.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: t.surface, zIndex: 10, flexShrink: 0,
      }}>
        <div>
          <div style={{
            fontSize: 20, fontWeight: 500,
            color: t.textPrimary, letterSpacing: -0.5,
            fontFamily: font.display,
          }}>
            {pageTitle}
          </div>
          <div style={{
            fontSize: 12, color: t.textMuted, fontFamily: font.mono,
          }}>
            {new Date().toLocaleDateString("en-US", {
              weekday: "long", month: "long", day: "numeric", year: "numeric",
            })}
          </div>
        </div>

        {/* Multi-portal switcher (Phase 3). Renders nothing for users
            with no memberships; a static chip for one; a dropdown for 2+. */}
        <PortalSwitcher
          user={user}
          activeTeamId={activeWorkspace}
          onSwitched={() => window.location.reload()}
        />

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: t.navyLight, padding: "5px 12px",
          }}>
            <div style={{
              width: 7, height: 7, borderRadius: "50%",
              background: "#22c55e",
              boxShadow: "0 0 6px #22c55e80",
              animation: "pulse 2s infinite",
            }} />
            <span style={{
              fontSize: 10, color: t.goldLight, fontWeight: 700,
              fontFamily: font.body, letterSpacing: "1px",
            }}>AI ACTIVE</span>
          </div>
          <div onClick={() => navigate("/settings")} style={{ cursor: "pointer" }}>
            {user.photo_url ? (
              <img src={user.photo_url} alt="" style={{
                width: 32, height: 32, borderRadius: "50%", objectFit: "cover",
              }} />
            ) : (
              <Avatar name={user.name} size={32} />
            )}
          </div>
        </div>
      </div>

      {/* Workspace context strip — appears under the main header when the
          user is viewing a team workspace. Paints in the team's primary
          color so the visual cue "you are in team mode" is unmissable. */}
      {user.team_id && activeWorkspace && (
        <div style={{
          padding: "6px 28px", display: "flex", alignItems: "center", gap: 8,
          background: `${t.emerald}0C`,
          borderBottom: `1px solid ${t.emerald}20`,
          transition: "all 0.3s ease",
          animation: "slideDown 0.25s ease",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke={t.emerald} strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <span style={{
            fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body,
          }}>Team Workspace</span>
          <span style={{
            fontSize: 11, color: t.textMuted, fontFamily: font.body,
          }}>· Showing team clients only</span>
        </div>
      )}
    </>
  );
}
